import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography } from "@mui/material";
import { patchInvestigation } from "../../../../../apis";
import { InvestigationSectionHeader } from "../components/InvestigationSectionHeader";
import { InvestigationAdvertisement } from "../components/InvestigationAdvertisement";

import { Board } from "./Board";
import { AddAndEditPlan } from "./AddPlan";
import { List } from "./List";
import { Separator } from "../components";
import { useConstants } from "../Main/useConstants";
import { ToggleButtons } from "../../../../MainComponents/ToggleButton/ToggleButton";

export const ActivityPlan = ({
  investigation,
  setInvestigation,
  justView = false,
  handleSelectedSection,
  reloadInvestigation,
}) => {
  const [t] = useTranslation("investigation");
  const { enums } = useConstants();
  
  const [plans, setPlans] = useState(investigation?.plans ?? []);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [view, setView] = useState("list");

  const options = [
    {
      icon: "outline_row_vertical",
      iconSelected: "bold_row_vertical",
      label: t("activityPlan.list"),
      value: "list"
    },
    {
      icon: "outline_kanban",
      iconSelected: "bold_kanban",
      label: t("activityPlan.board"),
      value: "board"
    },
  ]

  const somePlanExist = useMemo(() => {
    if (plans.length > 0) return true;
    return false;
  }, [plans]);

  useEffect(() => {
    if (investigation.plans != null) {
      setPlans(investigation.plans);
    } else {
      setPlans([]);
    }
  }, [investigation?.plans]);

  const handleSavePlans = async (newPlans, selectedActivity) => {
    const objToSave = {
      folder: {
        plans: newPlans,
      },
    };

    try {
      await patchInvestigation(investigation.alias, objToSave);
      setInvestigation({ ...investigation, plans: newPlans });
      setSelectedActivity(selectedActivity ?? null);
      setIsEditing(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box className="dyTheme1" p={1}>

      {!isEditing && !justView && (
        <InvestigationSectionHeader
          text={t("activityPlan.add_to_investigation")}
          buttonText={t("activityPlan.add")}
          buttonIcon="outline_origin_search"
          onClick={() => setIsEditing(true)}
        />
      )}

      {justView && (
        <Separator
          text={t("sections.activity_plan")}
          icon="outline_note_2"
          iconButton={handleSelectedSection ? "outline_edit" : undefined}
          onClick={handleSelectedSection ? () => handleSelectedSection(enums.sections.activityPlan) : undefined}
          sx={{ marginBottom: 1.5 }}
        />
      )}

      {!somePlanExist && !isEditing && (
        <InvestigationAdvertisement
          title={t("activityPlan.no_registered")}
          description={t("activityPlan.review_and_include")}
        />
      )}

      { view === "list" && somePlanExist && !isEditing && (
        <>
          {
            !justView && (
              <Box display="flex" justifyContent="flex-end" sx={{mt: 2, mr: 2}}>
                <ToggleButtons
                  options={options}
                  value={view}
                  onChange={setView}
                />
              </Box>
            )
          }
          <List
            plans={plans}
            setPlans={setPlans}
            handleSavePlans={handleSavePlans}
            setIsEditing={setIsEditing}
            setSelectedActivity={setSelectedActivity}
            reloadInvestigation={reloadInvestigation}
            justView={justView}
          />
        </>
      )}

      {view === "board" && somePlanExist && !isEditing && !justView && (
        <Box sx={{ padding: 2 }}>
          <Box display="flex" justifyContent="flex-end" sx={{ mb: 2}}>
            <ToggleButtons
              options={options}
              value={view}
              onChange={setView}
            />
          </Box>
          <Board
            plans={plans}
            setPlans={setPlans}
            selectedActivity={selectedActivity}
            setSelectedActivity={setSelectedActivity}
            setIsEditing={setIsEditing}
            handleSavePlans={handleSavePlans}
            justView={justView}
          />
        </Box>
      )}

      {isEditing && (
        <AddAndEditPlan
          plans={plans}
          setPlans={setPlans}
          selectedActivity={selectedActivity}
          setSelectedActivity={setSelectedActivity}
          setIsEditing={setIsEditing}
          handleSavePlans={handleSavePlans}
          investigation={investigation}
          setInvestigation={setInvestigation}
        />
      )}
    </Box>
  );
};
